import React, { useRef, useState, useCallback, useEffect } from "react";
import {
  LoadScript,
  GoogleMap,
  Marker,
  Autocomplete,
} from "@react-google-maps/api";
import toast from "react-hot-toast";
import {
  reverseGeocodeLatLng,
  fetchAddressFromCoords,
  extractAddressFromPlace,
} from "../../../Utils/locationUtils";

// ── Constants ─────────────────────────────────────────────────────────────────
const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 };
const DEFAULT_ZOOM = 5;
const PICKED_ZOOM = 16;
const MAP_CONTAINER_STYLE = {
  width: "100%",
  height: "260px",
  borderRadius: "12px",
};

const MAP_OPTIONS = {
  disableDefaultUI: true,
  zoomControl: true,
  clickableIcons: false,
  gestureHandling: "cooperative",
};

// ─────────────────────────────────────────────────────────────────────────────
const MapPicker = ({ apiKey, lat, lng, onAddressChange }) => {
  const [markerPos, setMarkerPos] = useState(null);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [locLoading, setLocLoading] = useState(false);

  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);

  // ── Sync props when lat/lng changes (Critical for Edit mode) ─────────────
  useEffect(() => {
    const isValidLat = typeof lat === "number" && !isNaN(lat);
    const isValidLng = typeof lng === "number" && !isNaN(lng);

    if (isValidLat && isValidLng) {
      const newPos = { lat, lng };
      setMarkerPos(newPos);
      setMapCenter(newPos);
      setZoom(PICKED_ZOOM);
    } else {
      // No valid coordinates → reset to default (for new address)
      setMarkerPos(null);
      setMapCenter(DEFAULT_CENTER);
      setZoom(DEFAULT_ZOOM);
    }
  }, [lat, lng]);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const applyAddress = useCallback(
    (addrObj) => {
      const newPos = { lat: addrObj.lat, lng: addrObj.lng };
      setMarkerPos(newPos);
      setMapCenter(newPos);
      setZoom(PICKED_ZOOM);
      onAddressChange(addrObj);
    },
    [onAddressChange]
  );

  // ── Event handlers ───────────────────────────────────────────────────────────
  const handlePlaceChanged = async () => {
    if (!autocompleteRef.current) return;
    const place = autocompleteRef.current.getPlace();
    if (!place?.geometry?.location) return;

    const pickedLat = place.geometry.location.lat();
    const pickedLng = place.geometry.location.lng();
    const addr = extractAddressFromPlace(place);

    applyAddress({ ...addr, lat: pickedLat, lng: pickedLng });
  };

  const handleMarkerDragEnd = useCallback(
    async (e) => {
      const newLat = e.latLng.lat();
      const newLng = e.latLng.lng();

      setMarkerPos({ lat: newLat, lng: newLng });

      try {
        const addr = await reverseGeocodeLatLng(newLat, newLng, apiKey);
        applyAddress(addr);
      } catch (err) {
        onAddressChange({ lat: newLat, lng: newLng });
        toast.error("Could not resolve address for this pin");
      }
    },
    [apiKey, applyAddress, onAddressChange]
  );

  const handleUseCurrentLocation = async () => {
    setLocLoading(true);
    try {
      const addr = await fetchAddressFromCoords(apiKey);
      applyAddress(addr);
      toast.success("Location detected");
    } catch (err) {
      toast.error(err.message || "Could not get location");
    } finally {
      setLocLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="relative w-full rounded-xl overflow-hidden border border-gray-200 shadow-sm">
      {/* Search bar */}
      <div className="absolute top-2 left-2 right-2 z-10">
        <Autocomplete
          onLoad={(ref) => (autocompleteRef.current = ref)}
          onPlaceChanged={handlePlaceChanged}
        >
          <div className="flex items-center bg-white rounded-lg shadow-md px-3 py-2 gap-2 border border-gray-200">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4 text-gray-400 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search location on map..."
              className="flex-1 text-sm outline-none bg-transparent text-gray-700 placeholder-gray-400"
            />
          </div>
        </Autocomplete>
      </div>

      {/* Google Map */}
      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        center={mapCenter}
        zoom={zoom}
        options={MAP_OPTIONS}
        onLoad={(map) => (mapRef.current = map)}
      >
        {markerPos && (
          <Marker
            position={markerPos}
            draggable
            onDragEnd={handleMarkerDragEnd}
            title="Drag to adjust location"
          />
        )}
      </GoogleMap>

      {/* Current Location button */}
      <button
        type="button"
        onClick={handleUseCurrentLocation}
        disabled={locLoading}
        className={`
          absolute bottom-1 right-3 z-10 flex items-center gap-1.5
          bg-white text-primaryColor text-xs font-medium
          px-3 py-2 rounded-lg shadow-md border border-gray-200
          hover:bg-primaryColor hover:text-white transition-all duration-200
          disabled:opacity-60 disabled:cursor-not-allowed
        `}
      >
        {locLoading ? (
          <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <circle cx="12" cy="12" r="3" />
            <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
            <circle cx="12" cy="12" r="8" strokeDasharray="2 2" />
          </svg>
        )}
        {locLoading ? "Locating…" : "My Location"}
      </button>

      {/* Hints */}
      {!markerPos && (
        <div className="absolute bottom-3 left-3 z-10 bg-black/50 text-white text-[10px] px-2 py-1 rounded-md pointer-events-none">
          Search or use My Location to drop a pin
        </div>
      )}
      {markerPos && (
        <div className="absolute bottom-3 left-3 z-10 bg-black/50 text-white text-[10px] px-2 py-1 rounded-md pointer-events-none">
          Drag the pin to fine-tune
        </div>
      )}
    </div>
  );
};

export default MapPicker;